import React, { useEffect, useRef } from 'react';
import { RHFTextField, FormProvider, RHFUploadSingleFile, NextImage } from '@sdf/base';
import { Styles } from '@t2c/shared-fe';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useAiText } from '../../../../t2c-fe/hooks/ai-text/useAiText';
import CopyToClipboard from 'react-copy-to-clipboard';
import LoadingButton from '@mui/lab/LoadingButton';
import CustomModal from '../../../Common/CustomModal';
import { CustomDialogTitle } from '../../../Common/dialog';
import { CreditDialog } from '../../create-video/CreditDialog';

export const AiTextWorkBench = () => {
  const {
    prompt,
    setPrompt,
    isGenerated,
    isLoading,
    handleClear,
    handleGenerate,
    showCreditDialog,
    handleCloseCreditDialog,
    handleUpgrade,
    onCopy,
    downloadTxtFile,
    showDynamicContent,
    setShowDynamicContent,
    displayedContent,
    isUserScrolling,
    handleClose,
    handleScroll,
    temporaryPrompt,
  } = useAiText();

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const maxCharLimit = 3500;

  useEffect(() => {
    if (!isUserScrolling && textAreaRef.current) {
      textAreaRef.current.scrollTop = textAreaRef.current.scrollHeight;
    }
  }, [displayedContent, isUserScrolling]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography sx={styles.heading}>What do you need help with? </Typography>
        <TextField
          placeholder="E.g., Write a script for a promotional video, brainstorm blog ideas, or draft an email."
          size="medium"
          type="text"
          name="prompt"
          multiline
          rows={14}
          sx={styles.promptTextField}
          value={prompt}
          inputProps={{ maxLength: maxCharLimit }}
          onChange={(e) => setPrompt(e.target.value?.slice(0, maxCharLimit))}
          fullWidth
        />
        <Box>
          <Typography
            variant="body2"
            sx={{ color: (theme) => theme.palette.text.placeholder, pl: '2px', pt: '2px' }}
          >
            {prompt?.length || 0}/{maxCharLimit} characters
          </Typography>
        </Box>
        <CustomModal open={showDynamicContent} maxWidth="md" styles={{ padding: '15px' }}>
          <CustomDialogTitle title={'AI Text'} showDivider={false} onClose={handleClose} />
          <Box
            ref={textAreaRef}
            sx={styles.outputField}
            onScrollCapture={(e) => {
              handleScroll(e);
            }}
            dangerouslySetInnerHTML={{ __html: displayedContent }}
          />
          <Box sx={styles.contentModal}>
            <Typography fontSize={16} fontWeight={500}>
              Save the text as a .TXT file
            </Typography>
            <Button onClick={downloadTxtFile}>
              <NextImage src={'/assets/icons/DownloadMenu.svg'} alt="" width={16} height={16} />
            </Button>
          </Box>

          <Box sx={styles.btns}>
            <Button
              variant="outlined"
              sx={styles.clearBtn}
              onClick={() => {
                setShowDynamicContent(false);
                handleClear();
              }}
              disabled={isLoading}
            >
              New Prompt
            </Button>

            <CopyToClipboard
              text={temporaryPrompt}
              onCopy={() => {
                setShowDynamicContent(false);
                onCopy();
              }}
            >
              <Button variant="contained" sx={styles.generateBtn}>
                Copy
                <NextImage
                  src={'/assets/icons/Copy.svg'}
                  alt=""
                  width={20}
                  height={20}
                  sx={{ paddingLeft: 1 }}
                />
              </Button>
            </CopyToClipboard>
          </Box>
        </CustomModal>
      </Grid>
      <Grid item xs={6} mt={1} />
      <Box display={'flex'} width={'100%'} gap={2} ml={3}>
        <Box sx={{ flex: 1 }}>
          {isGenerated && (
            <Button onClick={handleClear} variant="outlined" sx={styles.remakeBtn} fullWidth>
              Remake
            </Button>
          )}
        </Box>

        <Box sx={{ flex: 1 }}>
          <LoadingButton
            variant="contained"
            sx={styles.createBtn}
            onClick={handleGenerate}
            loading={isLoading}
            fullWidth
          >
            Create
            {!isLoading && (
              <NextImage
                sx={{ pl: 1 }}
                src={'/assets/icons/Ai.svg'}
                alt=""
                width={24}
                height={24}
              />
            )}
          </LoadingButton>
        </Box>
      </Box>

      <CreditDialog
        isOpen={showCreditDialog}
        onClose={handleCloseCreditDialog}
        onUpgrade={handleUpgrade}
        isFetchingCreditDetail={false}
        promptRestriction={true}
        upgradeButtonTitle={'Upgrade'}
      />
    </Grid>
  );
};

const styles: Styles = {
  promptTextField: {
    '& .MuiOutlinedInput-root': {
      border: '1px solid',
      borderRadius: '8px',
      borderColor: (theme) => theme.palette.background.ternary,
      backgroundColor: (theme) => theme.palette.background.secondary,
      '&:hover': {
        borderColor: (theme) => theme.palette.background.activeBorder,
      },
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: (theme) => theme.palette.text.placeholder,
    },
  },
  textField: {
    borderRadius: 4,
  },
  heading: {
    fontSize: 14,
    fontWeight: 500,
    color: (theme) => theme.palette.text.primary,
    marginBottom: '5px',
    marginTop: '5px',
  },
  createBtn: {
    py: 3,
    fontSize: 16,
    color: (theme) => theme.palette.text.primary,
    boxShadow: 'none',
    alignSelf: 'flex-end',
  },
  downloadBox: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: 2,
    ml: 3,
    mb: 3,
  },
  remakeBtn: {
    color: (theme) => theme.palette.text.primary,
    backgroundColor: (theme) => theme.palette.background.secondary,
    fontSize: '16px',
    fontWeight: 500,
    borderColor: (theme) => theme.palette.background.border,
    py: 3,
  },
  contentModal: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: 0.5,
    padding: '10px',
  },
  btns: {
    display: 'flex',
    width: '100%',
    gap: 2,
  },
  generateBtn: {
    flex: 1,
    color: (theme) => theme.palette.common.white,
    boxShadow: 'none',
    fontSize: '16px',
    fontWeight: 500,
  },
  clearBtn: {
    flex: 1,
    color: (theme) => theme.palette.common.white,
    fontSize: '16px',
    fontWeight: 500,
    borderColor: (theme) => theme.palette.background.cancelBtn,
  },
  outputField: {
    overflowY: 'auto',
    padding: '10px',
    height: '350px',
    border: '1px solid',
    borderRadius: '6px',
    borderColor: (theme) => theme.palette.background.border,
    backgroundColor: (theme) => theme.palette.background.secondary,
    '& p': {
      marginBottom: '16px',
    },
  },
};
